import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import MuxPlayer from "@mux/mux-player-react/lazy";
import axios from "axios";
import { API_URL } from "@shared/util/env";
import { fmtTC } from "@shared/util/brand";

const GUEST_NAME = "Guest";

function setMeta(property, content) {
  const attr = property.startsWith("twitter:") ? "name" : "property";
  let el = document.querySelector(`meta[${attr}="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

function timecodeToSeconds(tc) {
  if (!tc) return 0;
  const parts = String(tc).split(":").map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function avatarInitials(name) {
  const n = (name || "").trim();
  if (!n) return "G";
  return n.charAt(0).toUpperCase();
}

export default function ShareDripPoC() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [comments, setComments] = useState([]);
  const [threadInput, setThreadInput] = useState("");
  const [mode, setMode] = useState("watch");
  const playerRef = useRef(null);
  const threadRef = useRef(null);
  const apiBase = API_URL || `${window.location.origin}/api`;

  const active = data?.clips?.[activeIdx] || null;

  const fetchShare = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/share-drip/${token}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.msg || "Could not load share.");
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    fetchShare();
  }, [fetchShare]);

  useEffect(() => {
    async function loadComments() {
      if (!token || activeIdx < 0) return;
      try {
        const res = await axios.get(`${apiBase}/share-drip/${token}/comments/${activeIdx}`);
        setComments(res.data.map((c) => ({ name: c.userName, text: c.text })));
      } catch {
        setComments([]);
      }
    }
    loadComments();
  }, [apiBase, token, activeIdx]);

  useEffect(() => {
    threadRef.current?.scrollTo(0, threadRef.current.scrollHeight);
  }, [comments]);

  const socialImageUrl = data
    ? `https://image.mux.com/${data.playbackId}/thumbnail.png?time=${
        data.socialImageTime >= 0
          ? data.socialImageTime
          : timecodeToSeconds(data.clips[0]?.timecode)
      }`
    : null;

  useEffect(() => {
    if (!data || !socialImageUrl) return;
    document.title = data.videoTitle || "Share Drip";
    setMeta("og:title", data.videoTitle || "Share Drip");
    setMeta("og:image", socialImageUrl);
    setMeta("og:description", data.clips[0]?.quote || "");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", data.videoTitle || "Share Drip");
    setMeta("twitter:image", socialImageUrl);
  }, [data, socialImageUrl]);

  const sendThread = async () => {
    if (!threadInput.trim() || !data) return;
    const text = threadInput.trim();
    setThreadInput("");
    setComments((prev) => [...prev, { name: GUEST_NAME, text }]);
    try {
      await axios.post(`${apiBase}/share-drip/${token}/comments/${activeIdx}`, {
        userName: GUEST_NAME,
        text,
      });
    } catch (err) {
      console.error("[share-drip] comment error:", err);
    }
  };

  if (loading) {
    return (
      <>
        <style>{CSS_TEXT}</style>
        <main className="share-drip-poc-scope">
          <div className="wrap">
            <p className="muted2">Loading…</p>
          </div>
        </main>
      </>
    );
  }

  if (error || !data?.clips?.length) {
    return (
      <>
        <style>{CSS_TEXT}</style>
        <main className="share-drip-poc-scope">
          <div className="wrap">
            <div className="h2">Share unavailable</div>
            <p className="muted2">{error || "Invalid link."}</p>
          </div>
        </main>
      </>
    );
  }

  const windowLabel = active
    ? active.timecodeEnd
      ? `${fmtTC(active.timecode)}–${fmtTC(active.timecodeEnd)}`
      : fmtTC(active.timecode)
    : "";

  return (
    <>
      <style>{CSS_TEXT}</style>
      <main className="share-drip-poc-scope">
        <div className="topbar">
          <div className="topbar-inner">
            <div className="brand">
              <div className="logo">✦</div>
              <div>
                <h1 style={{ margin: 0, fontSize: 16, lineHeight: 1.1 }}>Share Drip PoC</h1>
                <div className="sub">{data.videoTitle || "Video"}</div>
              </div>
            </div>
            <div className="pill purple">{new Date(data.createdAt).toLocaleDateString()}</div>
          </div>
        </div>

        <div className="wrap">
          {socialImageUrl && (
            <div className="card" style={{ marginBottom: 14, padding: 0, overflow: "hidden" }}>
              <a href={socialImageUrl} target="_blank" rel="noopener noreferrer" download>
                <img
                  src={socialImageUrl}
                  alt={data.videoTitle || "Social preview"}
                  className="social-hero-img"
                />
              </a>
              <div style={{ padding: "10px 18px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="muted2" style={{ fontSize: 12 }}>Right-click or tap &amp; hold to save image</span>
                <a
                  href={socialImageUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-btn"
                >
                  Download
                </a>
              </div>
            </div>
          )}
          {data.senderMessage ? (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="section-title">Instructions</div>
              <div className="p" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                {data.senderMessage}
              </div>
            </div>
          ) : null}

          <div className="card clip-card">
            <div className="clip-header">
              <div className="section-title">Clip {activeIdx + 1}</div>
              <div className="toggle-pill">
                <button
                  type="button"
                  className={mode === "watch" ? "active" : ""}
                  onClick={() => setMode("watch")}
                >
                  Watch
                </button>
                <button
                  type="button"
                  className={mode === "read" ? "active" : ""}
                  onClick={() => setMode("read")}
                >
                  Read
                </button>
              </div>
            </div>

            {mode === "watch" ? (
              <div className="video-wrap">
                {data.playbackId && active ? (
                  <MuxPlayer
                    ref={playerRef}
                    key={`${activeIdx}-${active.timecode}`}
                    playbackId={data.playbackId}
                    startTime={timecodeToSeconds(active.timecode)}
                    preload="metadata"
                    accentColor="#8B5CF6"
                    className="mux-player-share-drip"
                    metadata={{ video_title: data.videoTitle }}
                  />
                ) : (
                  <div className="video">▶ No clip</div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
                  <div className="pill small">Window: {windowLabel}</div>
                </div>
              </div>
            ) : (
              <div className="readbox" style={{ display: "block" }}>
                <div className="quote">&ldquo;{active?.quote}&rdquo;</div>
              </div>
            )}
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <div className="section-title">Clips</div>
            <div className="muted2" style={{ marginTop: 8, marginBottom: 10 }}>
              Swipe or scroll to pick a clip. Comments are per clip.
            </div>
            <div className="thumb-scroller">
              {data.clips.map((c, idx) => {
                const t = timecodeToSeconds(c.timecode);
                const thumbSrc = data.playbackId
                  ? `https://image.mux.com/${data.playbackId}/thumbnail.jpg?time=${t + 2}`
                  : "";
                return (
                  <button
                    key={idx}
                    type="button"
                    className={"thumb-mini" + (idx === activeIdx ? " active" : "")}
                    onClick={() => setActiveIdx(idx)}
                  >
                    {thumbSrc ? (
                      <img src={thumbSrc} alt="" className="thumb-img" />
                    ) : (
                      <div className="thumb-fallback">{idx + 1}</div>
                    )}
                    <div className="thumb-cap">Clip {idx + 1}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <div className="section-title">Comments · Clip {activeIdx + 1}</div>
            <div className="muted2" style={{ marginTop: 8 }}>
              Thread for this clip only.
            </div>
            <div className="chat" ref={threadRef}>
              {comments.map((m, i) => (
                <div key={i} className="msg">
                  <div className="avatar">{avatarInitials(m.name)}</div>
                  <div>
                    <div className="name">{m.name}</div>
                    <div className="bubble">{m.text}</div>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <div className="muted2" style={{ textAlign: "center", padding: 20, fontSize: 14 }}>
                  No comments yet.
                </div>
              )}
            </div>
            <div className="chatbar">
              <input
                value={threadInput}
                onChange={(e) => setThreadInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendThread()}
                placeholder="Add a comment…"
              />
              <button className="btn primary" type="button" onClick={sendThread}>
                Send
              </button>
            </div>
          </div>

          <div className="footer">Share Drip · WebinarVault</div>
        </div>
      </main>
    </>
  );
}

const CSS_TEXT = `
  .share-drip-poc-scope,
  .share-drip-poc-scope * {
    box-sizing: border-box;
  }
  .share-drip-poc-scope main { margin: 0; }
  .share-drip-poc-scope {
    --bg: #070A12;
    --border: rgba(255,255,255,.10);
    --text: #f8fafc;
    --muted: #cbd5e1;
    --muted2: #94a3b8;
    --blue: #8B5CF6;
    --purple: #8B5CF6;
    --shadow: 0 14px 40px -26px rgba(0,0,0,.95);
    background: var(--bg);
    color: var(--text);
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
  }
  .share-drip-poc-scope .wrap { max-width: 420px; margin: 0 auto; padding: 16px 14px 64px; }
  .share-drip-poc-scope .topbar {
    position: sticky; top: 0; z-index: 20;
    backdrop-filter: blur(10px);
    background: rgba(7,10,18,.75);
    border-bottom: 1px solid var(--border);
  }
  .share-drip-poc-scope .topbar-inner {
    max-width: 420px; margin: 0 auto; padding: 12px 14px;
    display: flex; justify-content: space-between; gap: 10px; align-items: center;
  }
  .share-drip-poc-scope .brand { display: flex; gap: 10px; align-items: center; }
  .share-drip-poc-scope .logo {
    width: 38px; height: 38px; border-radius: 16px;
    border: 1px solid var(--border);
    background: linear-gradient(135deg, rgba(139,92,246,.35), rgba(255,255,255,.06));
    display: grid; place-items: center;
    font-weight: 900;
  }
  .share-drip-poc-scope .brand .sub { font-size: 13px; color: var(--muted2); margin-top: 3px; }
  .share-drip-poc-scope .pill {
    display: inline-flex; gap: 8px; align-items: center;
    border: 1px solid var(--border);
    background: rgba(255,255,255,.05);
    padding: 8px 12px; border-radius: 999px;
    font-size: 13px;
  }
  .share-drip-poc-scope .pill.small { padding: 6px 10px; font-size: 12px; }
  .share-drip-poc-scope .pill.purple { border-color: rgba(139,92,246,.45); background: rgba(139,92,246,.18); }
  .share-drip-poc-scope .card {
    background: linear-gradient(180deg, rgba(255,255,255,.10), rgba(255,255,255,.03));
    border: 1px solid var(--border);
    border-radius: 22px;
    padding: 18px;
    box-shadow: var(--shadow);
  }
  .share-drip-poc-scope .h2 { font-size: 28px; font-weight: 950; margin: 0; }
  .share-drip-poc-scope .section-title { font-size: 18px; font-weight: 950; margin: 0; }
  .share-drip-poc-scope .muted2 { color: var(--muted2); }
  .share-drip-poc-scope .p { font-size: 15px; line-height: 1.55; color: var(--muted); }
  .share-drip-poc-scope .quote {
    margin-top: 10px;
    padding: 14px;
    border-left: 4px solid rgba(139,92,246,.75);
    font-style: italic;
    line-height: 1.45;
    background: rgba(0,0,0,.18);
    border-radius: 16px;
    font-size: 18px;
  }
  .share-drip-poc-scope .clip-header {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
  }
  .share-drip-poc-scope .toggle-pill {
    display: inline-flex; gap: 4px; padding: 4px; border-radius: 999px;
    background: rgba(255,255,255,.06);
    border: 1px solid rgba(255,255,255,.10);
  }
  .share-drip-poc-scope .toggle-pill button {
    padding: 6px 14px; font-size: 12px; font-weight: 700; border: none; border-radius: 999px;
    background: transparent; color: var(--muted2); cursor: pointer;
  }
  .share-drip-poc-scope .toggle-pill button.active {
    background: rgba(139,92,246,.25); color: var(--text);
  }
  .share-drip-poc-scope mux-player.mux-player-share-drip {
    width: 100%; aspect-ratio: 16/9; border-radius: 18px; overflow: hidden;
  }
  .share-drip-poc-scope .video {
    background: #000; height: 220px; border-radius: 18px; display: flex; align-items: center; justify-content: center;
    color: #9aa3b2; border: 1px solid rgba(255,255,255,.08);
  }
  .share-drip-poc-scope .thumb-scroller {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 12px;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 10px;
    margin-bottom: 4px;
    scrollbar-width: auto;
    scrollbar-color: rgba(139,92,246,.65) rgba(255,255,255,.08);
  }
  .share-drip-poc-scope .thumb-scroller::-webkit-scrollbar {
    height: 12px;
  }
  .share-drip-poc-scope .thumb-scroller::-webkit-scrollbar-track {
    background: rgba(255,255,255,.08);
    border-radius: 8px;
  }
  .share-drip-poc-scope .thumb-scroller::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, rgba(139,92,246,.8), rgba(47,123,255,.6));
    border-radius: 8px;
  }
  .share-drip-poc-scope .thumb-mini {
    flex: 0 0 auto;
    width: 120px;
    border: 2px solid rgba(255,255,255,.12);
    border-radius: 14px;
    overflow: hidden;
    background: rgba(0,0,0,.25);
    cursor: pointer;
    padding: 0;
    text-align: left;
  }
  .share-drip-poc-scope .thumb-mini.active {
    border-color: rgba(139,92,246,.85);
    box-shadow: 0 0 0 2px rgba(139,92,246,.35);
  }
  .share-drip-poc-scope .thumb-img {
    width: 100%;
    aspect-ratio: 16/9;
    object-fit: cover;
    display: block;
  }
  .share-drip-poc-scope .thumb-fallback {
    aspect-ratio: 16/9;
    display: grid;
    place-items: center;
    font-weight: 950;
    font-size: 22px;
    color: var(--muted2);
  }
  .share-drip-poc-scope .thumb-cap {
    padding: 6px 8px;
    font-size: 11px;
    font-weight: 800;
    color: var(--muted);
  }
  .share-drip-poc-scope .chat {
    margin-top: 12px;
    max-height: 280px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .share-drip-poc-scope .msg { display: flex; gap: 10px; align-items: flex-start; }
  .share-drip-poc-scope .avatar {
    width: 34px; height: 34px; border-radius: 12px;
    background: rgba(139,92,246,.25);
    display: grid; place-items: center;
    font-weight: 900; font-size: 13px;
  }
  .share-drip-poc-scope .name { font-size: 12px; font-weight: 800; color: var(--muted); }
  .share-drip-poc-scope .bubble {
    margin-top: 4px;
    padding: 10px 12px;
    border-radius: 14px;
    background: rgba(255,255,255,.08);
    border: 1px solid rgba(255,255,255,.08);
    font-size: 14px;
    line-height: 1.4;
  }
  .share-drip-poc-scope .chatbar {
    margin-top: 12px;
    display: flex;
    gap: 8px;
  }
  .share-drip-poc-scope .chatbar input {
    flex: 1;
    border-radius: 14px;
    border: 1px solid rgba(255,255,255,.12);
    background: rgba(0,0,0,.2);
    color: var(--text);
    padding: 12px 12px;
    font-size: 14px;
  }
  .share-drip-poc-scope .btn.primary {
    border: 1px solid rgba(255,255,255,.12);
    background: linear-gradient(90deg, rgba(139,92,246,.35), rgba(47,123,255,.25));
    color: var(--text);
    border-radius: 14px;
    padding: 12px 14px;
    font-weight: 800;
    cursor: pointer;
  }
  .share-drip-poc-scope .social-hero-img {
    width: 100%;
    display: block;
    cursor: pointer;
  }
  .share-drip-poc-scope .download-btn {
    display: inline-block;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 800;
    border-radius: 10px;
    background: linear-gradient(90deg, rgba(139,92,246,.35), rgba(47,123,255,.25));
    border: 1px solid rgba(255,255,255,.12);
    color: var(--text);
    text-decoration: none;
    cursor: pointer;
  }
  .share-drip-poc-scope .footer {
    margin-top: 24px;
    text-align: center;
    font-size: 12px;
    color: var(--muted2);
  }
`;
